import { memo, useCallback, useEffect, useMemo, useRef } from "react"
import { useHomeSessionsV2 } from "@/common/queries/use-home-sessions-v2"
import { useBeUserData } from "@/common/queries/use-user"
import { useQuery } from "@tanstack/react-query"
import { ChevronRight } from "lucide-react"
import { get } from "radash"

import { v1GetTierName } from "@be-miniapp/api"
import { DISPLAY_VERSION } from "@be-miniapp/app-configs"
import { useTranslation } from "@be-miniapp/i18n"
import { RowButton } from "@be-miniapp/ui/components/row-button"
import { useNavigate } from "@be-miniapp/ui/hooks/use-navigate"

import type { HomeBackground } from "@/hooks/use-optimized-home-data"
import { Icon } from "@/components/ui/icons/icons"

const UserTab = memo(() => {
  const navigate = useNavigate()
  const userName =
    (useBeUserData((state) => state.data?.user_name) as
      | string
      | null
      | undefined) ?? ""
  const phoneNumber =
    (useBeUserData((state) => state.data?.phone_no) as
      | string
      | null
      | undefined) ?? ""

  const { t } = useTranslation()

  const { data: userLoyalty } = useQuery({
    queryKey: ["user-loyalty"],
    queryFn: () => v1GetTierName({}),
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: true,
  })

  const tierImage = get(userLoyalty, "data.tierImage", undefined) as
    | string
    | undefined

  const { data: homeSessions } = useHomeSessionsV2()

  const homeBackground = useMemo(() => {
    const backgroundList = get(homeSessions, "tier_background.tiers", [])
    const tier = get(userLoyalty, "data.tier", -1)

    if (tier === -1)
      return get(homeSessions, "tier_background.default", {}) as HomeBackground

    const tierBackground = backgroundList.find(
      (background: HomeBackground) => background.tier === tier
    )

    return (
      tierBackground ||
      (get(homeSessions, "tier_background.default", {}) as HomeBackground)
    )
  }, [homeSessions, userLoyalty])

  const preloadedRef = useRef<string | null>(null)
  useEffect(() => {
    const src = homeBackground?.home_content
    if (!src || src === preloadedRef.current) return
    preloadedRef.current = src
    const img = new Image()
    img.src = src
  }, [homeBackground?.home_content])

  const handleNavigateSupport = useCallback(
    () => navigate("/profile/support"),
    [navigate]
  )
  const handleNavigateSettings = useCallback(
    () => navigate("/profile/settings"),
    [navigate]
  )

  const backgroundStyle = useMemo(
    () =>
      homeBackground?.home_content
        ? {
            background: `url(${homeBackground.home_content})`,
            backgroundRepeat: "no-repeat" as const,
            backgroundSize: "cover" as const,
          }
        : undefined,
    [homeBackground?.home_content]
  )

  return (
    <div className="flex size-full min-h-0 flex-col overflow-y-auto bg-be-neutral-25">
      <div
        className="h-auto max-h-[40vh] w-full bg-cover py-6"
        style={backgroundStyle}
      >
        <div className="flex w-full flex-col items-center justify-center gap-1 pt-safe-top">
          <div className="px-4 text-center font-bold text-lg text-white [text-shadow:_2px_2px_4px_rgb(0_0_0_/_0.8)]">
            {userName}
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="font-medium text-white text-xs [text-shadow:_1px_1px_3px_rgb(0_0_0_/_0.7)]">
              {phoneNumber}
            </span>
          </div>

          {tierImage && (
            <div className="flex items-center gap-2">
              <img src={tierImage} className="size-6" />
            </div>
          )}
        </div>
      </div>

      <div className="w-full">
        <RowButton
          icon={<Icon.support size={16} />}
          onClick={handleNavigateSupport}
          suffix={<ChevronRight size={15} className="text-gray-500" />}
        >
          {t("user_support_title")}
        </RowButton>
      </div>

      <div className="w-full border-be-navy-100/60 border-t">
        <RowButton
          icon={<Icon.setting size={16} />}
          onClick={handleNavigateSettings}
          suffix={<ChevronRight size={15} className="text-gray-500" />}
        >
          {t("user.settings.title")}
        </RowButton>
      </div>

      <div className="mt-6 px-4">
        <span className="text-gray-500 text-xs">
          {t("common.version")} {DISPLAY_VERSION} (Webview)
        </span>
      </div>
    </div>
  )
})

UserTab.displayName = "UserTab"

export { UserTab }
